import React from "react";
import { getLocale, t } from "@auto/services";
import { GUI_LANGUAGE_CHANGE_EVENT } from "@auto/events/Gui";

import Alert from "@mui/material/Alert";

import EChart from "@src/reporting/components/EChart";
import useReportingResource from "@src/reporting/hooks/useReportingResource.mjs";
import { reportingResourceKey } from "@src/reporting/model/reportingFilters.mjs";
import { ageLabel } from "@src/reporting/model/reportingFormatters.mjs";
import { loadRegisteredVoterAgeDistribution } from "@src/reporting/services/reportingApi.mjs";

import styles from "@src/reporting/Reporting.module.styl";

const useReportingLocale = () => {
  const [locale, setLocale] = React.useState(() => getLocale());

  React.useEffect(() => {
    const onLanguageChange = (language) => setLocale(language || getLocale());
    GUI_LANGUAGE_CHANGE_EVENT.subscribe(onLanguageChange);
    return () => GUI_LANGUAGE_CHANGE_EVENT.unsubscribe(onLanguageChange);
  }, []);

  return locale;
};

const RegisteredVoterAgeDistributionWidget = ({ filter, refreshToken = 0 }) => {
  const locale = useReportingLocale();
  const resource = useReportingResource(
    () => loadRegisteredVoterAgeDistribution(filter),
    {
      key: reportingResourceKey("registered-voter-age", filter),
      refreshToken,
      initialData: [],
    },
  );

  const rows = (resource.data || []).filter((row) => {
    const key = String(row?.key || "").trim().toUpperCase();
    return key && key !== "UNDER_18" && key !== "UNKNOWN";
  });
  const option = React.useMemo(() => ({
    tooltip: { trigger: "axis", axisPointer: { type: "shadow" } },
    grid: { left: 42, right: 14, top: 34, bottom: 52, containLabel: true },
    xAxis: {
      type: "category",
      data: rows.map((row) => ageLabel(row.key)),
      axisLabel: { rotate: 28 },
    },
    yAxis: { type: "value", minInterval: 1 },
    series: [{
      type: "bar",
      data: rows.map((row) => row.count),
      label: { show: true, position: "top", fontSize: 10 },
      labelLayout: { hideOverlap: false },
    }],
  }), [rows, locale]);

  return (
    <div className={styles.innerChart}>
      <h3>{t("reporting.ageDistribution")}</h3>
      {resource.error ? <Alert severity="error">{resource.error}</Alert> : null}
      {!resource.error ? (
        <EChart option={option} loading={resource.loading} className={styles.mediumChart} />
      ) : null}
    </div>
  );
};

export default RegisteredVoterAgeDistributionWidget;
